import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";

import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Search,
  Plus,
  Users,
  MessageCircle,
  MoreVertical,
  Globe,
  Lock,
  Crown,
  Flag,
  Edit3,
  Trash2,
  X,
  TrendingUp,
  ChevronDown
} from "lucide-react";

import { fetchGroups, deleteGroup, leaveGroup } from "../../../redux/userGroups/groups/userGroupies";
import { fetchWatchlistThunk } from "../../../redux/user/watchlist/watchlistActions";

import ReportModal from "../../../components/report/ReportModal";
import GroupCredibilityChip from "../groupPageComponents/GroupCredibilityChip";
import StockLogo from "../../../components/StockLogo/StockLogoRender";
import RelevantGroupsRecommendation from "../groupPageComponents/RelevantGroups";
import MyGroupsNav from "../groupPageComponents/MyGroupsNav";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";



/* =====================================================
   NON-BLOCKING CONFIRM MODAL (ACCESSIBLE)
   ===================================================== */
const ConfirmModal = ({ open, title, message, onConfirm, onCancel, busy, error }) => {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 w-full max-w-md shadow-xl">
        <h3 className="text-gray-900 dark:text-white text-lg font-semibold mb-2">{title}</h3>

        <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">{message}</p>

        {error && (
          <p className="text-red-400 text-sm mb-4 -mt-2">{error}</p>
        )}

        <div className="flex justify-end space-x-3">
          <button
            disabled={busy}
            onClick={onCancel}
            className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-40 text-gray-700 dark:text-gray-200 transition"
          >
            {t('groups.listPage.cancel')}
          </button>

          <button
            disabled={busy}
            onClick={onConfirm}
            className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white transition"
          >
            {t('groups.listPage.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};




/* =====================================================
   ENHANCED GROUP CARD — ENTERPRISE-READY
   ===================================================== */
const EnhancedGroupCard = ({
  group,
  currentUser,
  onReport,
  onEdit,
  onDelete,
  onLeave
}) => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const nameRef = useRef(null);
  const [nameExpanded, setNameExpanded] = useState(false);
  const [nameTruncated, setNameTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const el = nameRef.current;
      if (!el || nameExpanded) return;
      setNameTruncated(el.scrollWidth > el.clientWidth);
    };
    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [group?.name, nameExpanded]);

  const userId = currentUser?.id;

  const members = Array.isArray(group?.members) ? group.members : [];
  const stocks = Array.isArray(group?.stocks) ? group.stocks : [];
  const posts = Array.isArray(group?.posts) ? group.posts : [];

  const isOwner =
    userId &&
    (group?.created_by === userId || group?.createdBy?.id === userId);

  const isMember =
    userId &&
    members.some(m => m.user_id === userId || m.id === userId);


  // click-outside handler only when open
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown)
      document.addEventListener("mousedown", handler);

    return () => document.removeEventListener("mousedown", handler);
  }, [showDropdown]);


  return (
    <div className="bg-cream-100 dark:bg-gray-800/50 rounded-2xl border border-cream-300 dark:border-gray-700/50 hover:border-cream-900/20 dark:hover:border-gray-600/50 transition-all duration-300 overflow-hidden group">

      {/* HEADER ======================================================= */}
      <div className="relative h-32 bg-gradient-to-br from-blue-600/30 to-purple-600/30">
        <div className="absolute inset-0 bg-black/20" />

        <div className="absolute top-4 right-4 flex items-center space-x-2">

          <div className="p-1.5 bg-black/20 rounded-lg backdrop-blur-sm">
            {group?.privacy === "public"
              ? <Globe className="text-green-400" size={16} />
              : <Lock className="text-yellow-400" size={16} />
            }
          </div>


          <div className="relative" ref={dropdownRef}>
            <button
              aria-haspopup="true"
              aria-expanded={showDropdown}
              aria-label={t('groups.listPage.groupOptionsAria')}
              onClick={() => setShowDropdown(v => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  setShowDropdown(v => !v);
              }}
              className="p-2 bg-black/20 hover:bg-black/40 rounded-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition"
            >
              <MoreVertical size={16} />
            </button>

            {showDropdown && (
              <div className="absolute right-0 top-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 w-48 rounded-xl z-50 shadow-xl">
                <div className="p-2">

                  {isOwner && (
                    <>
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          onEdit?.(group);
                        }}
                        className="w-full px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-left flex items-center space-x-2 text-gray-700 dark:text-gray-200"
                      >
                        <Edit3 size={16} />
                        <span>{t('groups.listPage.editGroup')}</span>
                      </button>

                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          onDelete?.(group);
                        }}
                        className="w-full px-3 py-2 rounded-lg hover:bg-red-900/20 text-left flex items-center space-x-2 text-red-700 dark:text-red-400"
                      >
                        <Trash2 size={16} />
                        <span>{t('groups.listPage.delete')}</span>
                      </button>
                    </>
                  )}

                  {!isOwner && (
                    <>
                      {isMember && (
                        <button
                          onClick={() => {
                            setShowDropdown(false);
                            onLeave?.(group);
                          }}
                          className="w-full px-3 py-2 rounded-lg hover:bg-yellow-900/20 text-left flex items-center space-x-2 text-yellow-400"
                        >
                          <X size={16} />
                          <span>{t('groups.listPage.leaveGroup')}</span>
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          onReport?.(group);
                        }}
                        className="w-full px-3 py-2 rounded-lg hover:bg-red-900/20 text-left flex items-center space-x-2 text-red-700 dark:text-red-400"
                      >
                        <Flag size={16} />
                        <span>{t('groups.listPage.report')}</span>
                      </button>
                    </>
                  )}

                </div>
              </div>
            )}
          </div>
        </div>


        {isOwner && (
          <div className="absolute bottom-4 left-4 bg-yellow-500/20 border border-yellow-500/30 rounded-full px-2 py-1 flex items-center space-x-2 text-xs">
            <Crown className="text-yellow-400" size={14} />
            <span>{t('groups.listPage.owner')}</span>
          </div>
        )}
      </div>



      {/* BODY ======================================================= */}
      <div className="p-6">

        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => nameTruncated && setNameExpanded((v) => !v)}
              className={`w-full text-left flex items-start gap-1.5 ${nameTruncated ? "cursor-pointer" : "cursor-default"}`}
              aria-expanded={nameTruncated ? nameExpanded : undefined}
            >
              <h3
                ref={nameRef}
                className={`text-xl font-bold text-gray-900 dark:text-white ${nameExpanded ? "" : "line-clamp-1"}`}
              >
                {group?.name}
              </h3>
              {nameTruncated && (
                <ChevronDown
                  size={18}
                  className={`flex-shrink-0 mt-1 text-gray-400 transition-transform ${nameExpanded ? "rotate-180" : ""}`}
                />
              )}
            </button>

            <div className="mt-1.5">
              <GroupCredibilityChip credibility={group?.credibility} />
            </div>

            <p className="text-gray-600 dark:text-gray-400 line-clamp-2 mt-2">
              {group?.description || t('groups.listPage.noDescription')}
            </p>
          </div>
        </div>



        {/* STOCKS ======================================================= */}
        {stocks.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="text-emerald-400" size={16} />
              <span className="text-gray-600 dark:text-gray-300 text-sm">{t('groups.listPage.associatedStocks')}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {stocks.slice(0, 4).map((s) => (
                <div
                  key={s?.id || s?.symbol}
                  className="bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-lg flex items-center gap-2"
                >
                  <StockLogo symbol={s?.symbol} size="w-5 h-5" />
                  <span className="text-emerald-300 text-xs">
                    {s?.symbol}
                  </span>
                </div>
              ))}

              {stocks.length > 4 && (
                <div className="bg-cream-100 dark:bg-gray-700/50 border border-cream-300 dark:border-gray-600/50 px-2 py-1 rounded-lg text-gray-600 dark:text-gray-300 text-xs">
                  {t('groups.listPage.moreCount', { count: stocks.length - 4 })}
                </div>
              )}
            </div>
          </div>
        )}



        {/* MEMBERS ======================================================= */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="flex gap-2 items-center">
              <Users className="text-blue-400" size={16} />
              <span className="text-gray-600 dark:text-gray-300 text-sm">
                {t('groups.listPage.membersCount', { count: members.length })}
              </span>
            </div>

            <span className="text-gray-600 dark:text-gray-400 text-xs">
              {group?.privacy === "public" ? t('groups.listPage.public') : t('groups.listPage.private')}
            </span>
          </div>


          <div className="flex -space-x-2">
            {members.slice(0, 5).map((m, idx) => (
              <UserAvatar
                key={m?.user_id || m?.id || idx}
                userId={m?.user_id || m?.id}
                username={m?.username}
                pictureUrl={m?.profile_picture ?? null}
                className="w-8 h-8 border-2 border-white dark:border-gray-800"
                textClass="text-xs"
              />
            ))}

            {members.length > 5 && (
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full border-2 border-white dark:border-gray-800 flex justify-center items-center text-gray-700 dark:text-gray-300 text-xs">
                +{members.length - 5}
              </div>
            )}
          </div>
        </div>



        {/* STATS ======================================================= */}
        <div className="bg-cream-100 dark:bg-gray-900/30 rounded-lg px-3 py-2 mb-4 flex items-center gap-3 text-sm">
          <MessageCircle className="text-green-400" size={14} />
          <span className="text-gray-600 dark:text-gray-400">{posts.length}</span>

          <div className="w-1 h-1 bg-gray-400 dark:bg-gray-600 rounded-full" />

          <span className="text-gray-600 dark:text-gray-400 text-xs">
            {(() => {
              const d = group?.createdAt || group?.created_at;
              return t('groups.listPage.created', { date: d ? new Date(d).toLocaleDateString() : t('groups.listPage.unknownDate') });
            })()}
          </span>
        </div>


        {/* CTA ======================================================= */}
        <Link
          to={`/groups/${group?.id}`}
          className="bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 rounded-xl w-full block text-center py-2 text-white font-medium"
        >
          {t('groups.listPage.viewGroup')}
        </Link>
      </div>
    </div>
  );
};
// =====================================================
// GROUP LIST PAGE — ENTERPRISE GRADE
// =====================================================
const GroupListPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all | owned | joined
  const [selectedStock, setSelectedStock] = useState(null);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);

  const [confirmState, setConfirmState] = useState({
    open: false,
    type: null,   // 'delete' | 'leave'
    target: null,
    busy: false,
    error: null
  });

  const { groups = [], status, error } = useSelector((s) => s.groups || {});
  const { user: currentUser } = useSelector((s) => s.auth || {});
  const { items: watchlist = [], fetched: watchlistFetched } = useSelector((s) => s.watchlist || {});



  // ---------------------------
  // INITIAL FETCH
  // ---------------------------
  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchGroups());
    }
  }, [dispatch, status]);

  // This page computes "Groups related to your watchlist" (relevantGroups,
  // below) directly from Redux's watchlist.items, but never fetched it
  // itself -- it silently relied on some other page (Dashboard, Watchlist)
  // having already populated that slice earlier in the session. On a fresh
  // direct visit to /groupies, watchlist.items was empty regardless of real
  // DB data, so the whole section (and #40's activeStock fix) could never
  // actually appear. Guarded on `fetched` rather than unconditional -- still
  // fetches on a genuinely fresh session, but a later remount of this page
  // (tab switch, route re-entry) no longer redundantly re-fetches a
  // watchlist this page (or another) already loaded.
  useEffect(() => {
    if (!watchlistFetched) dispatch(fetchWatchlistThunk({ skip: 0, limit: 50 }));
  }, [dispatch, watchlistFetched]);



  // ---------------------------
  // MEMOIZED COUNTS
  // ---------------------------
  const ownedGroupsCount = useMemo(
    () =>
      groups.filter(
        (g) =>
          g?.created_by === currentUser?.id ||
          g?.createdBy?.id === currentUser?.id
      ).length,
    [groups, currentUser]
  );

  const joinedGroupsCount = useMemo(
    () =>
      groups.filter(
        (g) =>
          Array.isArray(g?.members) &&
          g.members.some(
            // `m.id` is the GroupMember row's own primary key, not a user id --
            // comparing it against currentUser.id could false-positive whenever
            // a membership row's id happens to numerically match an unrelated
            // user's id. Only `m.user_id` actually identifies the member.
            (m) =>
              m.user_id === currentUser?.id &&
              g?.created_by !== currentUser?.id &&
              g?.createdBy?.id !== currentUser?.id
          )
      ).length,
    [groups, currentUser]
  );



  // ---------------------------
  // FILTERED GROUPS
  // ---------------------------
  const filteredGroups = useMemo(() => {
    if (!Array.isArray(groups)) return [];

    const q = searchTerm.trim().toLowerCase();

    return groups.filter((group) => {
      const name = group?.name?.toLowerCase() || "";
      const desc = group?.description?.toLowerCase() || "";

      // text match
      if (q && !(name.includes(q) || desc.includes(q))) return false;

      const isOwner =
        group?.created_by === currentUser?.id ||
        group?.createdBy?.id === currentUser?.id;

      const isMember =
        Array.isArray(group?.members) &&
        group.members.some(
          (m) => m.user_id === currentUser?.id || m.id === currentUser?.id
        );

      if (filter === "owned") return isOwner;
      if (filter === "joined") return isMember && !isOwner;

      return true;
    });
  }, [groups, searchTerm, filter, currentUser]);



  // ---------------------------
  // RELEVANT GROUPS TO WATCHLIST
  // ---------------------------
  const determineActivityLevel = (group) => {
    const posts = Array.isArray(group?.posts) ? group.posts.length : 0;
    const members = Array.isArray(group?.members) ? group.members.length : 0;

    if (posts > 50 || members > 100) return t('groups.listPage.activityLevels.veryActive');
    if (posts > 20 || members > 50) return t('groups.listPage.activityLevels.active');
    if (posts > 5 || members > 10) return t('groups.listPage.activityLevels.moderate');
    return t('groups.listPage.activityLevels.lowActivity');
  };

  const relevantGroups = useMemo(() => {
    if (!Array.isArray(groups) || !Array.isArray(watchlist)) return [];

    const wlSymbols = watchlist
      .map((w) => w?.symbol || w?.ticker)
      .filter(Boolean);

    if (wlSymbols.length === 0) return [];

    return groups
      .filter((g) => {
        const syms =
          (Array.isArray(g?.stocks) &&
            g.stocks.map((s) => s.symbol).filter(Boolean)) ||
          g?.tickers ||
          [];

        return Array.isArray(syms) && syms.some((s) => wlSymbols.includes(s));
      })
      .slice(0, 5)
      .map((g) => ({
        id: g.id,
        name: g.name,
        tickers:
          (Array.isArray(g?.stocks) &&
            g.stocks.map((s) => s.symbol).filter(Boolean)) ||
          g?.tickers ||
          [],
        members: Array.isArray(g?.members) ? g.members.length : 0,
        posts: Array.isArray(g?.posts) ? g.posts.length : 0,
        activity: determineActivityLevel(g),
        joined:
          Array.isArray(g?.members) &&
          g.members.some((m) => m.user_id === currentUser?.id || m.id === currentUser?.id)
      }));
  }, [groups, watchlist, currentUser]);



  // ---------------------------
  // REPORT HANDLERS
  // ---------------------------
  const openReportModal = (group) => {
    setReportTarget(group);
    setShowReportModal(true);
  };

  const closeReportModal = () => {
    setReportTarget(null);
    setShowReportModal(false);
  };



  // ---------------------------
  // CONFIRM ACTIONS
  // ---------------------------
  const requestDeleteGroup = (group) =>
    setConfirmState({ open: true, type: "delete", target: group, busy: false, error: null });

  const requestLeaveGroup = (group) =>
    setConfirmState({ open: true, type: "leave", target: group, busy: false, error: null });



  const handleConfirm = async () => {
    const { type, target } = confirmState;
    if (!target) return;

    setConfirmState((s) => ({ ...s, busy: true, error: null }));

    try {
      if (type === "delete") {
        await dispatch(deleteGroup(target.id)).unwrap();
      }

      if (type === "leave") {
        await dispatch(leaveGroup(target.id)).unwrap();
        // leaveGroup's reducer only updates `currentGroup`, not the list-page
        // `groups` array's membership flags -- re-fetch so this card's
        // Join/Leave state doesn't stay stuck showing "Leave" after success.
        dispatch(fetchGroups());
      }

      setConfirmState({ open: false, type: null, target: null, busy: false, error: null });
    } catch (err) {
      // Keep the modal open with the real error instead of silently closing --
      // a failed delete/leave that just disappears looks identical to a
      // succeeded one from the user's point of view.
      setConfirmState((s) => ({
        ...s,
        busy: false,
        error: err?.message || t('groups.listPage.actionFailedFallback', { action: type }),
      }));
    }
  };

  const handleCancelConfirm = () =>
    setConfirmState({ open: false, type: null, target: null, busy: false, error: null });



  // =====================================================
  // LOADING + ERROR UI
  // =====================================================
  if (status === "loading" && (!groups || groups.length === 0)) {
    return (
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div className="space-y-2">
              <div className="h-9 bg-gray-200 dark:bg-gray-700/50 rounded w-56 animate-pulse" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700/30 rounded w-72 animate-pulse" />
            </div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700/50 rounded-xl w-40 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-cream-100 dark:bg-gray-800/50 rounded-2xl border border-cream-300 dark:border-gray-700/50 overflow-hidden animate-pulse"
              >
                <div className="h-32 bg-gray-300/50 dark:bg-gray-700/50" />
                <div className="p-5 space-y-3">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700/50 rounded w-2/3" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700/30 rounded w-full" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700/30 rounded w-4/5" />
                  <div className="flex gap-2 pt-1">
                    <div className="h-6 bg-gray-200 dark:bg-gray-700/40 rounded-lg w-16" />
                    <div className="h-6 bg-gray-200 dark:bg-gray-700/40 rounded-lg w-16" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }


  if (error) {
    return (
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-2xl mb-4">{t('groups.listPage.errorLoadingGroups')}</div>

            <div className="text-gray-600 dark:text-gray-400 mb-6">{error}</div>

            <button
              type="button"
              onClick={() => dispatch(fetchGroups())}
              className="px-4 py-2 bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 text-white rounded-xl"
            >
              {t('groups.listPage.tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }



  // =====================================================
  // MAIN RENDER
  // =====================================================
  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-6 pb-20 flex items-center justify-center">
      <div className="max-w-7xl w-full">


        {/* HEADER ===================================================== */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{t('groups.listPage.heading')}</h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('groups.listPage.subtitle')}
            </p>
          </div>

          <Link
            to="/create-group"
            className="bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 text-white font-medium py-3 px-6 rounded-xl flex items-center space-x-2 transition shadow-lg shadow-violet-500/20"
          >
            <Plus size={20} />
            <span>{t('groups.listPage.createGroup')}</span>
          </Link>
        </div>



        {/* GRID LAYOUT ===================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">


          {/* MAIN COLUMN — order-2 on mobile so "Your Groups" (below) shows
              first without having to scroll past the whole groups grid. */}
          <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">


            {/* RELEVANT GROUPS ===================================================== */}
            {relevantGroups.length > 0 && (
              <section aria-label={t('groups.listPage.relatedToWatchlist')}>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                  {t('groups.listPage.relatedToWatchlist')}
                </h2>

                <RelevantGroupsRecommendation
                  groups={relevantGroups}
                  activeStock={selectedStock}
                  onActiveStockChange={setSelectedStock}
                  onViewAll={() => setFilter("all")}
                />
              </section>
            )}



            {/* SEARCH + FILTER ===================================================== */}
            <section aria-label={t('groups.listPage.searchFiltersAria')} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">

                <div className="flex-grow relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('groups.listPage.searchPlaceholder')}
                    className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-4 py-3 rounded-xl pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition"
                  />
                  <Search
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400"
                  />
                </div>


                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label={t('groups.listPage.filterGroupsAria')}
                  className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]"
                >
                  <option value="all">{t('groups.listPage.filterAll')}</option>
                  <option value="owned">{t('groups.listPage.filterOwned')}</option>
                  <option value="joined">{t('groups.listPage.filterJoined')}</option>
                </select>
              </div>
            </section>



            {/* STATS BAR ===================================================== */}
            <section aria-label={t('groups.listPage.statisticsAria')}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                <div className="bg-cream-100 dark:bg-gray-800/50 rounded-xl p-4 border border-cream-300 dark:border-gray-700/50">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 dark:bg-blue-500/10 p-2 rounded-lg">
                      <Users className="text-blue-700 dark:text-blue-400" size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredGroups.length}</p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">{t('groups.listPage.groupsFound')}</p>
                    </div>
                  </div>
                </div>


                <div className="bg-cream-100 dark:bg-gray-800/50 rounded-xl p-4 border border-cream-300 dark:border-gray-700/50">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 dark:bg-green-500/10 p-2 rounded-lg">
                      <Crown className="text-green-700 dark:text-green-400" size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{ownedGroupsCount}</p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">{t('groups.listPage.groupsOwned')}</p>
                    </div>
                  </div>
                </div>


                <div className="bg-cream-100 dark:bg-gray-800/50 rounded-xl p-4 border border-cream-300 dark:border-gray-700/50">
                  <div className="flex items-center space-x-3">
                    <div className="bg-violet-100 dark:bg-purple-500/10 p-2 rounded-lg">
                      <MessageCircle className="text-violet-700 dark:text-purple-400" size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{joinedGroupsCount}</p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">{t('groups.listPage.groupsJoined')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>



            {/* GROUPS GRID ===================================================== */}
            <section aria-label={t('groups.listPage.groupsListAria')}>
              {filteredGroups.length === 0 ? (
                <div className="text-center py-16">
                  <div className="bg-cream-100 dark:bg-gray-800/50 rounded-2xl p-12 border border-cream-300 dark:border-gray-700/50 max-w-md mx-auto">
                    <Users className="mx-auto text-gray-400 dark:text-gray-500 mb-6" size={64} />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      {searchTerm ? t('groups.listPage.noGroupsFound') : t('groups.listPage.noGroupsYet')}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                      {searchTerm
                        ? t('groups.listPage.noGroupsMatchSearch', { term: searchTerm })
                        : t('groups.listPage.beFirstToCreate')}
                    </p>

                    {!searchTerm && (
                      <Link
                        to="/create-group"
                        className="inline-flex items-center space-x-2 bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 text-white px-6 py-3 rounded-xl font-medium transition"
                      >
                        <Plus size={20} />
                        <span>{t('groups.listPage.createFirstGroup')}</span>
                      </Link>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {filteredGroups.map((group) => (
                    <EnhancedGroupCard
                      key={group.id}
                      group={group}
                      currentUser={currentUser}
                      onReport={openReportModal}
                      // Same destination as GroupDetailPage's own "Manage Group"
                      // button (onEdit={() => setActiveTab('about')}) -- that
                      // state lives locally in GroupDetailPage, so it's passed
                      // via route state rather than duplicating the edit UI here.
                      onEdit={(g) => navigate(`/groups/${g.id}`, { state: { openTab: 'about' } })}
                      onDelete={requestDeleteGroup}
                      onLeave={requestLeaveGroup}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>


          {/* RIGHT COLUMN — quick nav to groups you own, so you don't have
              to scan/search the full grid below to find your own group.
              order-1 on mobile: appears first (right after the page header),
              not after the whole groups grid, which would defeat the
              "quick nav" point on a phone. Reverts to genuinely right-side
              on lg: via col-span-1 + order-2. */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <MyGroupsNav groups={groups} currentUser={currentUser} variant="sidebar" />
          </div>
        </div>
      </div>



      {/* REPORT MODAL ===================================================== */}
      <ReportModal
        isOpen={showReportModal}
        onClose={closeReportModal}
        target={reportTarget}
        type="group"
        onSubmit={console.log}
      />



      {/* CONFIRM MODAL ===================================================== */}
      <ConfirmModal
        open={confirmState.open}
        busy={confirmState.busy}
        error={confirmState.error}
        title={
          confirmState.type === "delete"
            ? t('groups.listPage.confirmDeleteTitle')
            : confirmState.type === "leave"
            ? t('groups.listPage.confirmLeaveTitle')
            : t('groups.listPage.confirmDefaultTitle')
        }
        message={
          confirmState.type === "delete"
            ? t('groups.listPage.confirmDeleteMessage')
            : confirmState.type === "leave"
            ? t('groups.listPage.confirmLeaveMessage')
            : t('groups.listPage.confirmDefaultMessage')
        }
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
      />
    </div>
  );
};

export default GroupListPage;